// Copyright (c) 2024-present, Dinocollab Technologies, Inc. and its affiliates. All rights reserved.

import { CSSProperties, useMemo } from 'react'
import { VIEW_LIST_ROW_SPACING_VAR } from './styled'
import { columnsRecordToArray, resolveListCellPadding, resolveListNearEndThreshold, resolveListRowHeight, resolveListRowSpacing } from './helpers'
import type { TGetterId } from '../types'
import type { IViewListConfig, IViewListProps } from './types'

export function useMergedConfig<T>(config: IViewListConfig<T>, props: IViewListProps<T>) {
  return useMemo(() => {
    const density = props.density ?? config.density
    const rowSpacing = resolveListRowSpacing(props.spacing ?? config.spacing)
    const normalOptions = Object.assign({ autoHeight: false }, config.normalOptions, props.normalOptions)
    const virtualizedOptions = Object.assign({ overscan: 6 }, config.virtualizedOptions, props.virtualizedOptions)
    return {
      columns: columnsRecordToArray(props.value, props.columns ?? config.columns),
      selectable: props.selectable ?? config.selectable ?? false,
      rowHeight: resolveListRowHeight(density),
      cellPadding: resolveListCellPadding(density),
      rowSpacing,
      nearEndThreshold: resolveListNearEndThreshold(props.nearEndThreshold, config.nearEndThreshold),
      normalOptions,
      virtualizedOptions,
      rootStyle: { [VIEW_LIST_ROW_SPACING_VAR]: `${rowSpacing}px` } as CSSProperties
    }
  }, [props, config])
}

/**
 * Custom hook to manage list row selection logic.
 * Handles select all, individual row selection, and indeterminate state.
 *
 * @param value - The array of data items
 * @param getterId - Function to extract unique ID from each row
 * @param selectable - Whether selection is enabled
 * @param selectedIds - Controlled array of selected row IDs
 * @param onSelectionChange - Callback when selection changes
 * @returns Selection state and handlers
 */
export function useListSelection<T>(
  value: T[],
  getterId: TGetterId<T>,
  selectable: boolean,
  selectedIds?: (string | number)[],
  onSelectionChange?: (selectedIds: (string | number)[]) => void
) {
  const safeSelectedIds = selectedIds ?? []

  // Build map of row IDs for quick lookup
  const rowIdMap = useMemo(() => {
    const map = new Map<string | number, T>()
    value.forEach((row, index) => {
      const id = getterId(row, index)
      if (id !== null && id !== undefined) {
        map.set(id, row)
      }
    })
    return map
  }, [value, getterId])

  const allRowIds = useMemo(() => Array.from(rowIdMap.keys()), [rowIdMap])

  const isAllSelected = allRowIds.length > 0 && allRowIds.every((id) => safeSelectedIds.includes(id))
  const isSomeSelected = safeSelectedIds.length > 0 && !isAllSelected

  const handleToggleAll = () => {
    if (!selectable || !onSelectionChange) return
    if (isAllSelected) {
      onSelectionChange([])
    } else {
      onSelectionChange(allRowIds)
    }
  }

  const handleToggleRow = (rowId: string | number) => {
    if (!selectable || !onSelectionChange) return
    const newSelected = safeSelectedIds.includes(rowId) ? safeSelectedIds.filter((id) => id !== rowId) : [...safeSelectedIds, rowId]
    onSelectionChange(newSelected)
  }

  return {
    selectedIds: safeSelectedIds,
    isAllSelected,
    isSomeSelected,
    handleToggleAll,
    handleToggleRow
  }
}
